import { uiEn, uiEs, uiHi, type UiTree } from "@/i18n/ui";

export type LocaleCode = "en" | "es" | "hi";

type PageCopy = { title: string; description: string };

export type MessageTree = {
  app: {
    name: string;
    shortName: string;
    searchPlaceholder: string;
    signOut: string;
    signIn: string;
    toggleSidebar: string;
    toggleDark: string;
    loading: string;
  };
  nav: {
    dashboard: string;
    businessIntelligence: string;
    competitorIntelligence: string;
    seo: string;
    keywords: string;
    newsIntelligence: string;
    documents: string;
    content: string;
    social: string;
    youtube: string;
    campaigns: string;
    ads: string;
    imageStudio: string;
    videoStudio: string;
    landingPages: string;
    backlinks: string;
    websiteOptimizer: string;
    analytics: string;
    recommendations: string;
    workflow: string;
    integrations: string;
    observability: string;
    notifications: string;
    settings: string;
    profile: string;
    help: string;
  };
  sections: {
    main: string;
    create: string;
    optimize: string;
    system: string;
  };
  login: {
    title: string;
    subtitle: string;
    email: string;
    password: string;
    demoHint: string;
    preferCrm: string;
  };
  settings: {
    title: string;
    description: string;
    language: string;
    brandPack: string;
    brandClassic: string;
    brandSlate: string;
    brandTeal: string;
    appearance: string;
    tabGeneral: string;
    tabBusinessKnowledge: string;
  };
  pages: {
    dashboard: PageCopy;
    businessIntelligence: PageCopy;
    competitorIntelligence: PageCopy;
    seo: PageCopy;
    keywords: PageCopy;
    newsIntelligence: PageCopy;
    documents: PageCopy;
    content: PageCopy;
    social: PageCopy;
    youtube: PageCopy;
    campaigns: PageCopy;
    ads: PageCopy;
    imageStudio: PageCopy;
    videoStudio: PageCopy;
    landingPages: PageCopy;
    backlinks: PageCopy;
    websiteOptimizer: PageCopy;
    analytics: PageCopy;
    productUsage: PageCopy;
    recommendations: PageCopy;
    workflow: PageCopy;
    integrations: PageCopy;
    observability: PageCopy;
    search: PageCopy;
    profile: PageCopy;
    help: PageCopy;
  };
  ui: UiTree;
};

const enPages: MessageTree["pages"] = {
  dashboard: {
    title: "Marketing Dashboard",
    description: "Unified view of acquisition, content, paid media, SEO, and CRM lead counts (read-only).",
  },
  businessIntelligence: {
    title: "Business Intelligence",
    description: "SWOT, personas, and strategy packs generated for your brand — CRM remains the system of record for pipeline.",
  },
  competitorIntelligence: {
    title: "Competitor Intelligence",
    description: "Track domains, SEO posture, pricing tier, and SWOT signals — alerts land in Recommendations.",
  },
  seo: {
    title: "SEO Automation",
    description: "Page audits, meta/schema suggestions, and ranking hygiene. Keyword research lives in a dedicated module.",
  },
  keywords: {
    title: "Keyword Research",
    description: "Discover and cluster keywords. Results are stored per tenant via /api/marketing.",
  },
  newsIntelligence: {
    title: "News Intelligence",
    description: "Discover the latest and trending articles for your selected domain.",
  },
  documents: {
    title: "Business Knowledge",
    description: "Company profile and documents used to generate original, business-specific marketing content.",
  },
  content: {
    title: "AI Content Studio",
    description: "Create channel-ready drafts. Leads stay in CRM; content lives in MarketIQ.",
  },
  social: {
    title: "Social Media",
    description: "Caption + hashtag generation, approval-ready drafts, and a simple content calendar.",
  },
  youtube: {
    title: "YouTube",
    description: "Channel analytics, Shorts insights, video management, comments and publishing for your YouTube channel.",
  },
  campaigns: {
    title: "Campaign Manager",
    description: "Plan multi-channel campaigns. CRM remains the system of record for leads.",
  },
  ads: {
    title: "Advertisement Manager",
    description: "Campaign drafts plus blended spend/ROI from connected ad networks (sandbox or OAuth sync).",
  },
  imageStudio: {
    title: "Image Studio",
    description: "Banners, posters, infographics, carousels, and ad creatives — brief-first production queue.",
  },
  videoStudio: {
    title: "Video Studio",
    description: "Short videos, reels, product demos, and explainer scripts with VO/subtitle-ready structure.",
  },
  landingPages: {
    title: "Landing Page Builder",
    description: "AI outlines, SEO-friendly structure, and CRM-ready form placeholders for A/B experiments.",
  },
  backlinks: {
    title: "Backlinks",
    description: "Curated directory of backlink and profile sites for Indian businesses — filter by industry and country, then publish there yourself.",
  },
  websiteOptimizer: {
    title: "Website Optimizer",
    description: "Performance, accessibility, SEO, and conversion checks — additive guidance without touching CRM.",
  },
  analytics: {
    title: "Analytics",
    description: "Marketing funnel metrics plus MarketIQ product usage (page views inside MarketIQ).",
  },
  productUsage: {
    title: "MarketIQ product usage",
    description: "How your team uses MarketIQ (shell navigation). Stored in amos_audit_events only.",
  },
  recommendations: {
    title: "Recommendation Engine",
    description: "Every suggestion includes impact, effort, and an explanation — Recommendation Agent.",
  },
  workflow: {
    title: "Marketing Workflow",
    description: "Approval sequences plus Phase 3 background jobs (AI enrich, CRM refresh).",
  },
  integrations: {
    title: "Integrations",
    description: "OAuth + vendor account IDs unlock live GA4 / Meta / LinkedIn metrics. CRM remains read-only.",
  },
  observability: {
    title: "Observability",
    description: "Operator snapshot, connector alerts, and amos_audit_events for this tenant.",
  },
  search: {
    title: "Search",
    description: "Find campaigns, keywords, drafts, ads, and more across MarketIQ.",
  },
  profile: {
    title: "Profile",
    description: "Your MarketIQ identity.",
  },
  help: {
    title: "Help Center",
    description: "Guides for MarketIQ.",
  },
};

const esPages: MessageTree["pages"] = {
  dashboard: {
    title: "Panel de marketing",
    description: "Vista unificada de adquisición, contenido, medios pagos, SEO y conteos de leads del CRM (solo lectura).",
  },
  businessIntelligence: {
    title: "Inteligencia de negocio",
    description: "SWOT, personas y packs de estrategia para tu marca — el CRM sigue siendo la fuente de verdad del pipeline.",
  },
  competitorIntelligence: {
    title: "Inteligencia competitiva",
    description: "Sigue dominios, postura SEO, precios y señales SWOT — las alertas van a Recomendaciones.",
  },
  seo: {
    title: "Automatización SEO",
    description: "Auditorías, meta/schema y higiene de ranking. La investigación de keywords está en un módulo aparte.",
  },
  keywords: {
    title: "Investigación de keywords",
    description: "Descubre y agrupa keywords. Los resultados se guardan por tenant vía /api/marketing.",
  },
  newsIntelligence: {
    title: "Inteligencia de noticias",
    description: "Descubre los artículos más recientes y en tendencia para el dominio seleccionado.",
  },
  documents: {
    title: "Conocimiento del negocio",
    description: "Perfil y documentos de la empresa usados para generar contenido de marketing original y específico.",
  },
  content: {
    title: "Estudio de contenido IA",
    description: "Crea borradores listos por canal. Los leads quedan en el CRM; el contenido vive en MarketIQ.",
  },
  social: {
    title: "Redes sociales",
    description: "Captions, hashtags, borradores para aprobación y un calendario sencillo.",
  },
  youtube: {
    title: "YouTube",
    description: "Analíticas del canal, métricas de Shorts, gestión de vídeos, comentarios y publicación en tu canal de YouTube.",
  },
  campaigns: {
    title: "Gestor de campañas",
    description: "Planifica campañas multicanal. El CRM sigue siendo la fuente de verdad de leads.",
  },
  ads: {
    title: "Gestor de anuncios",
    description: "Borradores de campaña más gasto/ROI combinado de redes conectadas (sandbox u OAuth).",
  },
  imageStudio: {
    title: "Estudio de imagen",
    description: "Banners, posters, infografías, carruseles y creatividades — cola de producción por brief.",
  },
  videoStudio: {
    title: "Estudio de video",
    description: "Videos cortos, reels, demos y guiones explicativos con estructura lista para VO/subtítulos.",
  },
  landingPages: {
    title: "Constructor de landing",
    description: "Esquemas con IA, estructura SEO y formularios listos para CRM en experimentos A/B.",
  },
  backlinks: {
    title: "Backlinks",
    description: "Directorio curado de sitios de backlinks y perfiles para empresas de la India: filtra por industria y país y publica allí tú mismo.",
  },
  websiteOptimizer: {
    title: "Optimizador web",
    description: "Rendimiento, accesibilidad, SEO y conversión — guía aditiva sin tocar el CRM.",
  },
  analytics: {
    title: "Analítica",
    description: "Métricas de embudo más uso del producto MarketIQ (vistas de página en MarketIQ).",
  },
  productUsage: {
    title: "Uso del producto MarketIQ",
    description: "Cómo tu equipo usa MarketIQ (navegación). Solo se guarda en amos_audit_events.",
  },
  recommendations: {
    title: "Motor de recomendaciones",
    description: "Cada sugerencia incluye impacto, esfuerzo y explicación — Agente de recomendaciones.",
  },
  workflow: {
    title: "Flujo de marketing",
    description: "Secuencias de aprobación más trabajos en segundo plano (enriquecer IA, refrescar CRM).",
  },
  integrations: {
    title: "Integraciones",
    description: "OAuth + IDs de cuenta habilitan métricas en vivo de GA4 / Meta / LinkedIn. CRM solo lectura.",
  },
  observability: {
    title: "Observabilidad",
    description: "Resumen de operador, alertas de conectores y amos_audit_events de este tenant.",
  },
  search: {
    title: "Buscar",
    description: "Encuentra campañas, keywords, borradores, anuncios y más en MarketIQ.",
  },
  profile: {
    title: "Perfil",
    description: "Tu identidad en MarketIQ.",
  },
  help: {
    title: "Centro de ayuda",
    description: "Guías de MarketIQ.",
  },
};

const hiPages: MessageTree["pages"] = {
  dashboard: {
    title: "मार्केटिंग डैशबोर्ड",
    description: "एक्विज़िशन, कंटेंट, पेड मीडिया, SEO और CRM लीड काउंट्स का एकीकृत दृश्य (रीड-ओनली)।",
  },
  businessIntelligence: {
    title: "बिजनेस इंटेलिजेंस",
    description: "आपके ब्रांड के लिए SWOT, पर्सना और रणनीति पैक — पाइपलाइन का सिस्टम ऑफ़ रिकॉर्ड CRM ही रहता है।",
  },
  competitorIntelligence: {
    title: "प्रतिस्पर्धी इंटेलिजेंस",
    description: "डोमेन, SEO मुद्रा, प्राइसिंग और SWOT सिग्नल ट्रैक करें — अलर्ट Recommendations में जाते हैं।",
  },
  seo: {
    title: "SEO ऑटोमेशन",
    description: "पेज ऑडिट, मेटा/स्कीमा सुझाव और रैंकिंग स्वच्छता। कीवर्ड रिसर्च अलग मॉड्यूल में है।",
  },
  keywords: {
    title: "कीवर्ड रिसर्च",
    description: "कीवर्ड खोजें और क्लस्टर करें। परिणाम /api/marketing के ज़रिए टेनेंट-वाइज़ सेव होते हैं।",
  },
  newsIntelligence: {
    title: "न्यूज़ इंटेलिजेंस",
    description: "अपने चुने हुए डोमेन के लिए ताज़ा और ट्रेंडिंग लेख खोजें।",
  },
  documents: {
    title: "बिजनेस नॉलेज",
    description: "कंपनी प्रोफ़ाइल और दस्तावेज़ — मूल, व्यवसाय-विशिष्ट मार्केटिंग कंटेंट बनाने के लिए।",
  },
  content: {
    title: "AI कंटेंट स्टूडियो",
    description: "चैनल-रेडी ड्राफ्ट बनाएँ। लीड CRM में रहती हैं; कंटेंट MarketIQ में।",
  },
  social: {
    title: "सोशल मीडिया",
    description: "कैप्शन + हैशटैग, अप्रूवल-रेडी ड्राफ्ट और सरल कंटेंट कैलेंडर।",
  },
  youtube: {
    title: "YouTube",
    description: "आपके YouTube चैनल के लिए चैनल एनालिटिक्स, Shorts इनसाइट्स, वीडियो प्रबंधन, कमेंट और पब्लिशिंग।",
  },
  campaigns: {
    title: "कैंपेन मैनेजर",
    description: "मल्टी-चैनल कैंपेन प्लान करें। लीड का सिस्टम ऑफ़ रिकॉर्ड CRM ही रहता है।",
  },
  ads: {
    title: "विज्ञापन मैनेजर",
    description: "कैंपेन ड्राफ्ट और कनेक्टेड ऐड नेटवर्क से ब्लेंडेड स्पेंड/ROI (सैंडबॉक्स या OAuth)।",
  },
  imageStudio: {
    title: "इमेज स्टूडियो",
    description: "बैनर, पोस्टर, इन्फोग्राफ़िक, कैरसेल और ऐड क्रिएटिव — ब्रीफ-फ़र्स्ट प्रोडक्शन क्यू।",
  },
  videoStudio: {
    title: "वीडियो स्टूडियो",
    description: "शॉर्ट वीडियो, रील्स, प्रोडक्ट डेमो और एक्सप्लेनर स्क्रिप्ट — VO/सबटाइटल-रेडी संरचना।",
  },
  landingPages: {
    title: "लैंडिंग पेज बिल्डर",
    description: "AI आउटलाइन, SEO-फ्रेंडली संरचना और A/B के लिए CRM-रेडी फ़ॉर्म प्लेसहोल्डर।",
  },
  backlinks: {
    title: "बैकलिंक्स",
    description: "भारतीय व्यवसायों के लिए बैकलिंक व प्रोफ़ाइल साइटों की चयनित सूची — उद्योग और देश से फ़िल्टर करें, फिर वहाँ खुद पब्लिश करें।",
  },
  websiteOptimizer: {
    title: "वेबसाइट ऑप्टिमाइज़र",
    description: "परफ़ॉर्मेंस, ऐक्सेसिबिलिटी, SEO और कन्वर्ज़न चेक — CRM को छुए बिना एडिटिव गाइडेंस।",
  },
  analytics: {
    title: "एनालिटिक्स",
    description: "मार्केटिंग फ़नल मेट्रिक्स और MarketIQ प्रोडक्ट यूसेज (MarketIQ के अंदर पेज व्यू)।",
  },
  productUsage: {
    title: "MarketIQ प्रोडक्ट यूसेज",
    description: "आपकी टीम MarketIQ कैसे इस्तेमाल करती है (नेविगेशन)। केवल amos_audit_events में।",
  },
  recommendations: {
    title: "सिफ़ारिश इंजन",
    description: "हर सुझाव में प्रभाव, प्रयास और स्पष्टीकरण — Recommendation Agent।",
  },
  workflow: {
    title: "मार्केटिंग वर्कफ़्लो",
    description: "अप्रूवल सीक्वेंस और बैकग्राउंड जॉब्स (AI enrich, CRM refresh)।",
  },
  integrations: {
    title: "इंटिग्रेशन",
    description: "OAuth + अकाउंट ID से लाइव GA4 / Meta / LinkedIn मेट्रिक्स। CRM रीड-ओनली।",
  },
  observability: {
    title: "ऑब्ज़र्वेबिलिटी",
    description: "ऑपरेटर स्नैपशॉट, कनेक्टर अलर्ट और इस टेनेंट के amos_audit_events।",
  },
  search: {
    title: "खोज",
    description: "MarketIQ में कैंपेन, कीवर्ड, ड्राफ्ट, ऐड्स और और खोजें।",
  },
  profile: {
    title: "प्रोफ़ाइल",
    description: "आपकी MarketIQ पहचान।",
  },
  help: {
    title: "हेल्प सेंटर",
    description: "MarketIQ गाइड।",
  },
};

export const en: MessageTree = {
  app: {
    name: "MarketIQ",
    shortName: "MIQ",
    searchPlaceholder: "Search campaigns, keywords, creatives…",
    signOut: "Sign out",
    signIn: "Sign in",
    toggleSidebar: "Toggle sidebar",
    toggleDark: "Toggle dark mode",
    loading: "Loading…",
  },
  nav: {
    dashboard: "Dashboard",
    businessIntelligence: "Business Intelligence",
    competitorIntelligence: "Competitor Intelligence",
    seo: "SEO Automation",
    keywords: "Keyword Research",
    newsIntelligence: "News Intelligence",
    documents: "Business Knowledge",
    content: "AI Content Studio",
    social: "Social Media",
    youtube: "YouTube",
    campaigns: "Campaign Manager",
    ads: "Advertisement Manager",
    imageStudio: "Image Studio",
    videoStudio: "Video Studio",
    landingPages: "Landing Page Builder",
    backlinks: "Backlinks",
    websiteOptimizer: "Website Optimizer",
    analytics: "Analytics",
    recommendations: "Recommendation Engine",
    workflow: "Marketing Workflow",
    integrations: "Integrations",
    observability: "Observability",
    notifications: "Notifications",
    settings: "Settings",
    profile: "Profile",
    help: "Help",
  },
  sections: {
    main: "Intelligence",
    create: "Create & Distribute",
    optimize: "Optimize",
    system: "System",
  },
  login: {
    title: "MarketIQ",
    subtitle: "Sign in with your MarketIQ account.",
    email: "Email",
    password: "Password",
    demoHint: "Demo login uses the seeded MarketIQ users.",
    preferCrm: "Prefer CRM?",
  },
  settings: {
    title: "Settings",
    description: "Tenant preferences, locale, and brand pack. Auth stays in MarketIQ.",
    language: "Interface language",
    brandPack: "Brand pack",
    brandClassic: "Classic Klein Blue",
    brandSlate: "Slate Indigo",
    brandTeal: "Teal Signal",
    appearance: "Language & brand",
    tabGeneral: "General",
    tabBusinessKnowledge: "Business Knowledge",
  },
  pages: enPages,
  ui: uiEn,
};

export const es: MessageTree = {
  app: {
    name: "MarketIQ",
    shortName: "MIQ",
    searchPlaceholder: "Buscar campañas, palabras clave, creativos…",
    signOut: "Cerrar sesión",
    signIn: "Iniciar sesión",
    toggleSidebar: "Alternar barra lateral",
    toggleDark: "Alternar modo oscuro",
    loading: "Cargando…",
  },
  nav: {
    dashboard: "Panel",
    businessIntelligence: "Inteligencia de negocio",
    competitorIntelligence: "Inteligencia competitiva",
    seo: "Automatización SEO",
    keywords: "Investigación de keywords",
    newsIntelligence: "Inteligencia de noticias",
    documents: "Conocimiento del negocio",
    content: "Estudio de contenido IA",
    social: "Redes sociales",
    youtube: "YouTube",
    campaigns: "Gestor de campañas",
    ads: "Gestor de anuncios",
    imageStudio: "Estudio de imagen",
    videoStudio: "Estudio de video",
    landingPages: "Constructor de landing",
    backlinks: "Backlinks",
    websiteOptimizer: "Optimizador web",
    analytics: "Analítica",
    recommendations: "Motor de recomendaciones",
    workflow: "Flujo de marketing",
    integrations: "Integraciones",
    observability: "Observabilidad",
    notifications: "Notificaciones",
    settings: "Ajustes",
    profile: "Perfil",
    help: "Ayuda",
  },
  sections: {
    main: "Inteligencia",
    create: "Crear y distribuir",
    optimize: "Optimizar",
    system: "Sistema",
  },
  login: {
    title: "MarketIQ",
    subtitle: "Inicia sesión con tu cuenta de MarketIQ.",
    email: "Correo",
    password: "Contraseña",
    demoHint: "El acceso demo usa usuarios sembrados de MarketIQ.",
    preferCrm: "¿Prefieres el CRM?",
  },
  settings: {
    title: "Ajustes",
    description: "Preferencias del tenant, idioma y paquete de marca. Auth permanece en MarketIQ.",
    language: "Idioma de la interfaz",
    brandPack: "Paquete de marca",
    brandClassic: "Azul Klein clásico",
    brandSlate: "Índigo pizarra",
    brandTeal: "Señal verdeazulado",
    appearance: "Idioma y marca",
    tabGeneral: "General",
    tabBusinessKnowledge: "Conocimiento del negocio",
  },
  pages: esPages,
  ui: uiEs,
};

export const hi: MessageTree = {
  app: {
    name: "MarketIQ",
    shortName: "MIQ",
    searchPlaceholder: "कैंपेन, कीवर्ड, क्रिएटिव खोजें…",
    signOut: "साइन आउट",
    signIn: "साइन इन",
    toggleSidebar: "साइडबार टॉगल",
    toggleDark: "डार्क मोड टॉगल",
    loading: "लोड हो रहा है…",
  },
  nav: {
    dashboard: "डैशबोर्ड",
    businessIntelligence: "बिजनेस इंटेलिजेंस",
    competitorIntelligence: "प्रतिस्पर्धी इंटेलिजेंस",
    seo: "SEO ऑटोमेशन",
    keywords: "कीवर्ड रिसर्च",
    newsIntelligence: "न्यूज़ इंटेलिजेंस",
    documents: "बिजनेस नॉलेज",
    content: "AI कंटेंट स्टूडियो",
    social: "सोशल मीडिया",
    youtube: "YouTube",
    campaigns: "कैंपेन मैनेजर",
    ads: "विज्ञापन मैनेजर",
    imageStudio: "इमेज स्टूडियो",
    videoStudio: "वीडियो स्टूडियो",
    landingPages: "लैंडिंग पेज बिल्डर",
    backlinks: "बैकलिंक्स",
    websiteOptimizer: "वेबसाइट ऑप्टिमाइज़र",
    analytics: "एनालिटिक्स",
    recommendations: "सिफ़ारिश इंजन",
    workflow: "मार्केटिंग वर्कफ़्लो",
    integrations: "इंटिग्रेशन",
    observability: "ऑब्ज़र्वेबिलिटी",
    notifications: "नोटिफ़िकेशन",
    settings: "सेटिंग्स",
    profile: "प्रोफ़ाइल",
    help: "मदद",
  },
  sections: {
    main: "इंटेलिजेंस",
    create: "बनाएँ और वितरित करें",
    optimize: "ऑप्टिमाइज़",
    system: "सिस्टम",
  },
  login: {
    title: "MarketIQ",
    subtitle: "अपने MarketIQ अकाउंट से साइन इन करें।",
    email: "ईमेल",
    password: "पासवर्ड",
    demoHint: "डेमो लॉगिन MarketIQ में सीड किए यूज़र्स से चलता है।",
    preferCrm: "CRM पसंद है?",
  },
  settings: {
    title: "सेटिंग्स",
    description: "टेनेंट प्राथमिकताएँ, भाषा और ब्रांड पैक। ऑथ MarketIQ में रहता है।",
    language: "इंटरफ़ेस भाषा",
    brandPack: "ब्रांड पैक",
    brandClassic: "क्लासिक क्लेन ब्लू",
    brandSlate: "स्लेट इंडिगो",
    brandTeal: "टील सिग्नल",
    appearance: "भाषा और ब्रांड",
    tabGeneral: "सामान्य",
    tabBusinessKnowledge: "बिजनेस नॉलेज",
  },
  pages: hiPages,
  ui: uiHi,
};

export const LOCALES: Record<LocaleCode, MessageTree> = { en, es, hi };

export const LOCALE_LABELS: Record<LocaleCode, string> = {
  en: "English",
  es: "Español",
  hi: "हिन्दी",
};
